import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import API_BASE_URL from "config/api";

const INITIAL_CLIENT_USER_FORM = {
  clientName: "",
  email: "",
  name: "",
  phoneNumber: "",
  subscriptionPlanId: "",
  domain: "",
  defaultLanguage: "tr",
};

const USER_LIST_CACHE_KEY = "UserListData";
const SUBSCRIPTION_PLANS_CACHE_KEY = "SubscriptionPlansData";

const SESSION_KEYS_TO_CLEAR = [
  "userData",
  "userRole",
  "bannerSettings",
  "userCookies",
  "userMeData",
  "userCookieSummary",
  "integrationStatus",
  USER_LIST_CACHE_KEY,
  SUBSCRIPTION_PLANS_CACHE_KEY,
];

const getAuthToken = () =>
  typeof window !== "undefined" ? window.sessionStorage.getItem("authToken") : null;

const authRequest = async (path, options = {}) => {
  const token = getAuthToken();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });

    const text = await response.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = null;
      }
    }

    if (!response.ok) {
      const error = new Error((data && data.message) || response.statusText || "İstek başarısız oldu.");
      error.response = { data };
      throw error;
    }

    return data;
  } catch (error) {
    if (!error.response) {
      error.response = { data: { message: error.message || "Bir ağ hatası oluştu." } };
    }
    throw error;
  }
};

const clearUserSessionCaches = () => {
  if (typeof window === "undefined") return;
  SESSION_KEYS_TO_CLEAR.forEach((key) => {
    window.sessionStorage.removeItem(key);
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* ignore storage restrictions */
    }
  });
};

const storeImpersonatedSession = async (token) => {
  if (typeof window === "undefined") return null;

  const previousToken = window.sessionStorage.getItem("authToken");
  window.sessionStorage.setItem("authToken", token);

  let meData;
  try {
    meData = await authRequest("/me");
  } catch (error) {
    if (previousToken) {
      window.sessionStorage.setItem("authToken", previousToken);
    } else {
      window.sessionStorage.removeItem("authToken");
    }
    throw error;
  }

  clearUserSessionCaches();
  window.sessionStorage.setItem("authToken", token);

  if (meData) {
    try {
      window.sessionStorage.setItem("userMeData", JSON.stringify(meData));
    } catch (e) {
      /* ignore serialization issues */
    }

    const rawRole = meData?.user?.role;
    const normalizedRole =
      typeof rawRole === "string" ? rawRole.toLowerCase() : rawRole;
    const summary = meData?.user
      ? {
          userId: meData.user.id,
          email: meData.user.email,
          role: normalizedRole,
        }
      : null;

    if (summary) {
      window.sessionStorage.setItem("userData", JSON.stringify(summary));
    }

    if (normalizedRole) {
      window.sessionStorage.setItem("userRole", normalizedRole);
    }
  }

  return meData;
};

function UserManagement() {
  const [rows, setRows] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientUserForm, setClientUserForm] = useState(INITIAL_CLIENT_USER_FORM);
  const [creatingClientUser, setCreatingClientUser] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('user');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  // Billing form state
  const [billingForm, setBillingForm] = useState({
    name: "",
    address: "",
    city: "",
    district: "",
    country: "Turkey",
    postalCode: "",
    taxOffice: "",
    taxNumber: "",
    vatNumber: "",
    state: "",
    eInvoiceEnabled: true,
  });

  const seedFromCache = () => {
    try {
      const raw = sessionStorage.getItem(USER_LIST_CACHE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return false;
      setRows(parsed.filter((u) => u.role === "user"));
      return true;
    } catch {
      return false;
    }
  };

  const seedPlansFromCache = () => {
    try {
      const raw = sessionStorage.getItem(SUBSCRIPTION_PLANS_CACHE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return false;
      setSubscriptionPlans(parsed);
      return true;
    } catch {
      return false;
    }
  };

  const fetchUsers = async () => {
    setError("");
    setLoadingUsers(true);
    try {
      const response = await authRequest("/admin/users");
      const list = Array.isArray(response) ? response : [];
      const enriched = list.map((u) => ({
        ...u,
        clientId: Array.isArray(u.clients) && u.clients[0] ? u.clients[0].id : null,
        clientName: Array.isArray(u.clients) && u.clients[0] ? u.clients[0].name : null,
      }));
      const onlyUsers = enriched.filter((u) => u.role === "user");
      setRows(onlyUsers);
      sessionStorage.setItem(USER_LIST_CACHE_KEY, JSON.stringify(enriched));
    } catch (err) {
      setError(err.response?.data?.message || "Kullanıcılar alınamadı.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await authRequest("/admin/subscription-plans");
      const list = Array.isArray(response) ? response : [];
      setSubscriptionPlans(list);
      sessionStorage.setItem(SUBSCRIPTION_PLANS_CACHE_KEY, JSON.stringify(list));
    } catch (err) {
      // plans yüklenemese de kullanıcı yönetimi çalışsın; hata mesajını büyütmeyelim
    } finally {
      setLoadingPlans(false);
    }
  };

  const reloadUsers = async (bustCache = false) => {
    if (bustCache) sessionStorage.removeItem(USER_LIST_CACHE_KEY);
    const had = !bustCache && seedFromCache();
    await fetchUsers();
    return had;
  };

  const readRoleFromSession = () => {
    // 1) Simple key used in your screenshot
    const direct = sessionStorage.getItem('userRole');
    if (direct) {
      // it may be already a plain string like "superadmin"
      try {
        const parsed = JSON.parse(direct);
        if (typeof parsed === 'string') return parsed;
        if (parsed && typeof parsed.role === 'string') return parsed.role;
      } catch {
        return direct; // plain string
      }
    }

    // 2) userData shape: { userId, email, role, ... }
    try {
      const rawUserData = sessionStorage.getItem('userData');
      if (rawUserData) {
        const u = JSON.parse(rawUserData);
        if (typeof u?.role === 'string') return u.role;
      }
    } catch {}

    // 3) previous cache shape: { user: { role } }
    try {
      const raw = sessionStorage.getItem('userMeData');
      if (raw) {
        const parsed = JSON.parse(raw);
        const role = parsed?.user?.role;
        if (typeof role === 'string') return role;
      }
    } catch {}

    return 'user';
  };

  // Helpers for TL formatting and plan splitting
  const formatTL = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const monthlyPlans = subscriptionPlans.filter(p => (p.billingCycle || '').toLowerCase() === 'monthly');
  const yearlyPlans = subscriptionPlans.filter(p => (p.billingCycle || '').toLowerCase() === 'yearly');

  useEffect(() => {
    setCurrentUserRole(readRoleFromSession());
    seedFromCache();
    seedPlansFromCache();
    fetchUsers();
    fetchSubscriptionPlans();
  }, []);

  const handleClientUserChange = (event) => {
    const { name, value } = event.target;
    setClientUserForm((prev) => ({ ...prev, [name]: value }));
  };
  // Billing form handlers
  const handleBillingChange = (event) => {
    const { name, value } = event.target;
    setBillingForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleBillingSwitch = (event) => {
    const { name, checked } = event.target;
    setBillingForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleCreateClientUser = async (event) => {
    event.preventDefault();
    setCreatingClientUser(true);
    setSuccess("");
    setError("");
    const payload = {
      ...clientUserForm,
      billingInfo: billingForm,
      subscriptionPlanId: clientUserForm.subscriptionPlanId ? Number(clientUserForm.subscriptionPlanId) : undefined,
    };
    // Validation: subscription plan must be selected
    if (!payload.subscriptionPlanId) {
      setError('Lütfen bir abonelik planı seçin.');
      setCreatingClientUser(false);
      return;
    }
    try {
      await authRequest("/admin/create-client-user", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setSuccess("Müşteri ve kullanıcı başarıyla oluşturuldu.");
      setClientUserForm(INITIAL_CLIENT_USER_FORM);
      fetchUsers();
      setOpenCreate(false);
    } catch (err) {
      setError(err.response?.data?.message || "Kullanıcı oluşturulamadı.");
    } finally {
      setCreatingClientUser(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteUser) return;
    setDeletingUser(true);
    setError('');
    setSuccess('');
    try {
      await authRequest(`/admin/users/${confirmDeleteUser.id}`, {
        method: "DELETE",
      });
      setRows((prev) => prev.filter((r) => r.id !== confirmDeleteUser.id));
      setSuccess('Kullanıcı silindi.');
      setConfirmDeleteUser(null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Kullanıcı silinemedi.');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (!targetUser?.id) return;
    setError('');
    setSuccess('');
    setImpersonatingId(targetUser.id);

    try {
      const response = await authRequest("/admin/impersonate", {
        method: "POST",
        body: JSON.stringify({ userId: targetUser.id }),
      });

      const token = response?.token;
      if (!token) {
        throw new Error("İmpersonation token alınamadı.");
      }

      await storeImpersonatedSession(token);
      setSuccess(`${targetUser.email} kullanıcısına geçiş yapılıyor...`);
      window.location.replace("/dashboards");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "İmpersonate işlemi başarısız oldu.");
    } finally {
      setImpersonatingId(null);
    }
  };

  const isSuperAdmin = currentUserRole === 'superadmin';

  const columns = [
    { field: "id", headerName: "User ID", width: 120, sortable: true },
    { field: "email", headerName: "E-posta", flex: 1, minWidth: 200, sortable: true },
    {
      field: "status",
      headerName: "Durum",
      width: 140,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ textTransform: "capitalize" }}>{params.value || "-"}</Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Oluşturulma",
      width: 200,
      sortable: true,
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      renderCell: (params) => (
        <Box component="span">
          {params.row.createdAt ? new Date(params.row.createdAt).toLocaleDateString("tr-TR") : "-"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "İşlemler",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }} justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleImpersonate(params.row);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            tabIndex={-1}
            sx={{
              color: '#fff',
              borderColor: '#000000',
              '&:hover': {
                backgroundColor: '#fff',
                color: '#000000',
                borderColor: '#000000'
              }
            }}
            disabled={impersonatingId === params.row.id}
          >
            {impersonatingId === params.row.id ? 'Geçiliyor...' : 'İmpersonate'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: 'red',
              borderColor: 'red',
              '&:hover': {
                backgroundColor: 'red',
                color: '#fff',
                borderColor: 'red'
              }
            }}
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteUser(params.row); }}
            onMouseDown={(e) => e.stopPropagation()}
            tabIndex={-1}
            disabled={!isSuperAdmin}
          >
            Sil
          </Button>
        </Stack>
      ),
    },
  ];

  const filteredRows = rows.filter((u) => {
    const matchEmail = (u.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || u.status === statusFilter;
    return matchEmail && matchStatus;
  });

  return (
    <DashboardLayout>
      <Stack spacing={3} mb={3}>
        <Typography variant="h4" fontWeight="bold">Kullanıcı Yönetimi</Typography>
        <Typography variant="body2" color="text.secondary">
          Superadmin ve admin yetkileri ile tüm kullanıcıları görüntüleyebilir, silebilir ve yeni müşteri kullanıcıları oluşturabilirsiniz.
        </Typography>
      </Stack>

      {error && <Box mb={3}><Alert severity="error">{error}</Alert></Box>}
      {success && <Box mb={3}><Alert severity="success">{success}</Alert></Box>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexGrow={1}>
                  <TextField
                    label="E-posta ile ara"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', md: 240 }, maxWidth: 300, flexShrink: 0 }}
                  />
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 140,
                      flexShrink: 0,
                      '& .MuiInputBase-root': {
                        height: '40px', // match TextField small height
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <InputLabel>Durum</InputLabel>
                    <Select label="Durum" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
                      <MenuItem value="all">Tümü</MenuItem>
                      <MenuItem value="active">Aktif</MenuItem>
                      <MenuItem value="pending">Beklemede</MenuItem>
                      <MenuItem value="invited">Davet Gönderildi</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => reloadUsers(true)}
                    sx={{
                      flexShrink: 0,
                      color: '#000',
                      '&:hover': {
                        borderColor: 'inherit'
                      }
                    }}
                  >
                    Yenile
                  </Button>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreate(true)}
                  size="medium"
                  sx={{ color: '#fff' }}
                >
                  Yeni Müşteri
                </Button>
              </Stack>

              <Box sx={{ height: 520, width: '100%', mt: 1 }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  loading={loadingUsers}
                  disableSelectionOnClick
                  sortingOrder={["desc", "asc"]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableColumnSelector
                  disableColumnMenu
                  sx={{
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                  }}
                />
              </Box>

              <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="xl" fullWidth>
                <DialogTitle>Yeni Müşteri Oluştur</DialogTitle>
                <Box component="form" onSubmit={handleCreateClientUser}>
                  <DialogContent dividers>
                    <Grid container spacing={2}>
                      {/* Sol Kolon: Form + Fatura */}
                      <Grid item xs={12} md={8}>
                        <Stack spacing={2}>
                          <Grid container spacing={1}>
                            {/* 1. Satır: Domain + Varsayılan Dil (küçük kutu) */}
                            <Grid item xs={12} md={9}>
                              <TextField label="Domain" name="domain" value={clientUserForm.domain} onChange={handleClientUserChange} required fullWidth />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField label="Dil (tr, en)" name="defaultLanguage" value={clientUserForm.defaultLanguage} onChange={handleClientUserChange} required fullWidth />
                            </Grid>

                            {/* 2. Satır: Müşteri Adı + Kullanıcı Adı */}
                            <Grid item xs={12} md={6}>
                              <TextField label="Marka Adı" name="clientName" value={clientUserForm.clientName} onChange={handleClientUserChange} required fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField label="Müşteri Adı Soyadı" name="name" value={clientUserForm.name} onChange={handleClientUserChange} required fullWidth />
                            </Grid>

                            {/* 3. Satır: E‑posta + Telefon */}
                            <Grid item xs={12} md={6}>
                              <TextField label="E-posta Adresi" name="email" type="email" value={clientUserForm.email} onChange={handleClientUserChange} required fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField label="Telefon Numarası" name="phoneNumber" value={clientUserForm.phoneNumber} onChange={handleClientUserChange} required fullWidth />
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Fatura Bilgileri</Typography>
                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={12} md={6}>
                              <TextField label="Ünvan / Ad Soyad" name="name" value={billingForm.name} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField label="Adres" name="address" value={billingForm.address} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField label="İlçe" name="district" value={billingForm.district} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField label="İl" name="city" value={billingForm.city} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField label="Posta Kodu" name="postalCode" value={billingForm.postalCode} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField label="Vergi Dairesi" name="taxOffice" value={billingForm.taxOffice} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField label="Vergi/T.C. No" name="taxNumber" value={billingForm.taxNumber} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            {/* Kaldırıldı: VAT No & Eyalet/Bölge */}
                            <Grid item xs={12} md={6}>
                              <TextField label="Ülke" name="country" value={billingForm.country} onChange={handleBillingChange} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControlLabel
                                control={<Switch name="eInvoiceEnabled" checked={!!billingForm.eInvoiceEnabled} onChange={handleBillingSwitch} />}
                                label="E‑Fatura Aktif"
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Grid>

                      {/* Sağ Kolon: Plan Seçimi */}
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Planını Seç</Typography>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button
                              size="small"
                              onClick={() => setClientUserForm(prev => ({ ...prev, __cycle: 'monthly', subscriptionPlanId: '' }))}
                              sx={{
                                border: '1px solid #000',
                                color: clientUserForm.__cycle === 'monthly' ? '#fff' : '#000',
                                backgroundColor: clientUserForm.__cycle === 'monthly' ? '#000' : '#fff'
                              }}
                            >
                              Aylık
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setClientUserForm(prev => ({ ...prev, __cycle: 'yearly', subscriptionPlanId: '' }))}
                              sx={{
                                border: '1px solid #000',
                                color: clientUserForm.__cycle === 'yearly' ? '#fff' : '#000',
                                backgroundColor: clientUserForm.__cycle === 'yearly' ? '#000' : '#fff'
                              }}
                            >
                              Yıllık
                            </Button>
                          </Stack>
                          <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto' }}>
                            {(clientUserForm.__cycle === 'yearly' ? yearlyPlans : monthlyPlans).map((p) => (
                              <Box key={p.id} sx={{
                                p: 1,
                                borderRadius: 1,
                                border: clientUserForm.subscriptionPlanId === p.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                bgcolor: clientUserForm.subscriptionPlanId === p.id ? 'rgba(25,118,210,0.04)' : 'background.paper'
                              }}>
                                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                  <Box>
                                    <Typography fontWeight={700}>{p.name}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                      {p.billingCycle === 'yearly' ? `₺${formatTL(p.price || 0)}/yıl` : `₺${formatTL(p.price || 0)}/ay`}
                                      {p.hasMultilanguage ? ' · Çoklu dil' : ''}
                                      {p.isTrial ? ' · Trial' : ''}
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setClientUserForm(prev => ({ ...prev, subscriptionPlanId: p.id }));
                                    }}
                                    sx={{ color: '#fff', minWidth: 96 }}
                                  >
                                    {clientUserForm.subscriptionPlanId === p.id ? 'Seçildi' : 'Seçin'}
                                  </Button>
                                </Stack>
                              </Box>
                            ))}
                            {loadingPlans && (
                              <Typography variant="caption" color="text.secondary">Planlar yükleniyor…</Typography>
                            )}
                            {!loadingPlans && (clientUserForm.__cycle === 'yearly' ? yearlyPlans : monthlyPlans).length === 0 && (
                              <Typography variant="caption" color="text.secondary">Plan bulunamadı</Typography>
                            )}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenCreate(false)}>Vazgeç</Button>
                    <Button variant="contained" type="submit" disabled={creatingClientUser} sx={{ color: '#fff' }}>
                      {creatingClientUser ? "Oluşturuluyor..." : "Oluştur"}
                    </Button>
                  </DialogActions>
                </Box>
              </Dialog>

              <Dialog
                open={Boolean(confirmDeleteUser)}
                onClose={() => setConfirmDeleteUser(null)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle>Hesabı Sil</DialogTitle>
                <DialogContent dividers>
                  Bu hesabı silmek istediğinize emin misiniz?
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmDeleteUser(null)}>Vazgeç</Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirmDelete}
                    disabled={deletingUser}
                    sx={{ color: '#fff' }}
                  >
                    {deletingUser ? 'Siliniyor...' : 'Sil'}
                  </Button>
                </DialogActions>
              </Dialog>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Footer />
    </DashboardLayout>
  );
}

export default UserManagement;
